import React from "react";
import { Box, Skeleton, Stack } from "@mui/material";

interface FlexibleCardModuleSkeletonProps {
  reverse?: boolean;
  bgColor?: string;
}

const FlexibleCardModuleSkeleton = ({
  reverse = true,
  bgColor = "transparent"
}: FlexibleCardModuleSkeletonProps) => (
  <Box
    data-testid="flexible-card-module-skeleton"
    component="section"
    className="fluid-box"
    sx={{ backgroundColor: bgColor }}
  >
    <Box
      className="full-width-container"
      sx={{
        padding: { xs: "4rem 2.4rem", md: "4rem 3.2rem", lg: "6rem 10rem" }
      }}
    >
      <Stack
        gap={{ xs: "2.4rem", desktop: 0 }}
        sx={{
          display: "flex",
          flexDirection: {
            desktop: reverse ? "row-reverse" : "row",
            legacyTablet: "column"
          },
          width: "100%"
        }}
      >
        {/* Image Skeleton - Matching dimensions from flexible-card.tsx */}
        <Box
          flexShrink={0}
          sx={{
            width: { desktop: "60.1rem", legacyTablet: "100%", legacyMobile: "100%" },
            height: { desktop: "47.7rem", legacyTablet: "30rem", legacyMobile: "22.5rem" },
            position: "relative"
          }}
        >
          <Skeleton
            variant="rectangular"
            sx={{
              width: "100%",
              height: "100%",
              borderRadius: "2rem"
            }}
            data-testid="flexible-card-image-skeleton"
          />
        </Box>

        {/* Content Skeleton */}
        <Box
          sx={{
            display: "flex",
            padding: {
              xs: 0,
              desktop: reverse ? "6rem 6rem 0rem 2rem" : "6rem 2rem 0rem 6rem"
            },
            flexDirection: "column",
            alignItems: "flex-start",
            gap: { xs: "1.2rem", sm: "2.4rem" },
            flex: "1 0 0"
          }}
        >
          {/* Header Skeleton */}
          <Skeleton
            variant="text"
            sx={{
              width: { xs: "80%", sm: "60%" },
              height: { xs: "3rem", sm: "4rem" },
              borderRadius: "0.4rem"
            }}
            data-testid="flexible-card-header-skeleton"
          />

          {/* Copy Text Skeleton */}
          <Box sx={{ width: "100%" }}>
            <Skeleton
              variant="text"
              sx={{
                width: "100%",
                height: "3rem",
                mb: "0.8rem",
                borderRadius: "0.4rem"
              }}
              data-testid="flexible-card-copy-skeleton-1"
            />
            <Skeleton
              variant="text"
              sx={{
                width: "95%",
                height: "3rem",
                mb: "0.8rem",
                borderRadius: "0.4rem"
              }}
              data-testid="flexible-card-copy-skeleton-2"
            />
            <Skeleton
              variant="text"
              sx={{
                width: "85%",
                height: "3rem",
                borderRadius: "0.4rem"
              }}
              data-testid="flexible-card-copy-skeleton-3"
            />
          </Box>

          {/* CTA Button Skeleton */}
          <Box
            sx={{
              display: "flex",
              alignItems: { desktop: "flex-start", legacyTablet: "center", legacyMobile: "center" },
              justifyContent: {
                desktop: "flex-start",
                legacyTablet: "center",
                legacyMobile: "center"
              }
            }}
          >
            <Skeleton
              variant="rectangular"
              sx={{
                width: { xs: "12rem", sm: "15rem" },
                height: "4rem",
                borderRadius: "10rem"
              }}
              data-testid="flexible-card-cta-skeleton"
            />
          </Box>
        </Box>
      </Stack>
    </Box>
  </Box>
);

export default FlexibleCardModuleSkeleton;
